import { adminFetch } from '@/lib/apiClient';

/**
 * 엑셀 "다운로드 요청 → 백그라운드 생성 → 내역에서 받기" 공용 도우미(주문·상품 엑셀).
 * 두 BE(order-service, catalog-service)는 상태값·받기 링크·오류 형식이 같다.
 */
export type ExportJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';

/** 진행 중(목록을 계속 다시 불러와야 하는) 상태. */
export const isExportJobInProgress = (job: { status: string }): boolean =>
  job.status === 'PENDING' || job.status === 'RUNNING';

/** 진행 중인 요청이 있는 동안만 다시 불러오는 간격. */
export const EXPORT_JOBS_POLL_MS = 4000;

/** PENDING·RUNNING 이 하나라도 있으면 폴링 간격, 모두 끝났으면 false(멈춤). */
export const exportJobsPollInterval = (items: ReadonlyArray<{ status: string }> | undefined): number | false =>
  items?.some(isExportJobInProgress) ? EXPORT_JOBS_POLL_MS : false;

/** 삭제 가능 — 생성 중(RUNNING)만 불가(BE 409). */
export const canDeleteExportJob = (job: { status: string }): boolean => job.status !== 'RUNNING';

export const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ───────────────────────── 오류 ─────────────────────────

export class ExportJobError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    /** BE 메시지를 그대로 보여 줄 오류인지. false 면 호출부가 상태별 공통 문구를 쓴다. */
    readonly showBeMessage: boolean,
  ) {
    super(message);
  }
}

/** 오류 응답(JSON envelope) → ExportJobError. passthroughCodes 에 든 코드만 BE 메시지를 그대로 보인다. */
export const toExportJobError = async (
  res: Response,
  passthroughCodes: ReadonlySet<string>,
): Promise<ExportJobError> => {
  let code: string | null = null;
  let message = '';
  try {
    const env = (await res.json()) as { error?: { code?: string; message?: string } | null };
    code = env?.error?.code ?? null;
    message = env?.error?.message ?? '';
  } catch {
    // 본문 없음/비 JSON — 상태 코드로만 처리.
  }
  return new ExportJobError(message, res.status, code, Boolean(message) && code != null && passthroughCodes.has(code));
};

// ───────────────────────── 파일 받기 ─────────────────────────

/** 받기 링크 응답. direct=true 면 S3 서명 링크(5분), false(로컬)면 API 경로. */
interface DownloadUrl {
  url: string;
  fileName: string | null;
  expiresAt: string | null;
  direct: boolean;
}

const clickLink = (href: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = href;
  if (fileName) link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 파일 받기. 받을 때마다 링크를 새로 발급받는다(5분 만료라 캐시하지 않음).
 *  - direct(QA·운영): 브라우저가 S3 에서 직접 받는다 — 서버 구간 시간 제한이 없고, 현재 창 이동이라
 *    팝업 차단도 없다(다운로드라 화면은 그대로). 파일명은 S3 Content-Disposition. fetch 가 아니라 CORS 불필요.
 *  - 로컬(direct=false): adminFetch → blob → fileName 저장.
 */
export const downloadViaLink = async (
  downloadUrlPath: string,
  fallbackFileName: string,
  toError: (res: Response) => Promise<ExportJobError>,
): Promise<void> => {
  const linkRes = await adminFetch(downloadUrlPath);
  if (!linkRes.ok) throw await toError(linkRes);
  const { data } = (await linkRes.json()) as { data: DownloadUrl };
  if (data.direct) {
    clickLink(data.url);
    return;
  }
  const res = await adminFetch(data.url);
  if (!res.ok) throw await toError(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  clickLink(url, data.fileName ?? fallbackFileName);
  URL.revokeObjectURL(url);
};
